<template>
  <div class="marketing-hub">
    <header class="hub-header">
      <div>
        <h1>Marketing</h1>
        <p class="hub-subtitle">
          Growth and outreach across Praetorian products — leads, contacts, quotes,
          learner engagement, and campaigns you run from Account in every app.
        </p>
      </div>
    </header>

    <div class="marketing-bento">
      <section v-if="canViewGrowth" class="bento-purpose bento-activity">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-bolt"></i>
        </div>
        <div class="purpose-body">
          <h2>Agent activity</h2>
          <p>
            Live engagement feed — link opens, uploads, sales interest, and
            policy statements an agent left on a certificate. Open a row to see
            what we showed them and audit that result.
          </p>
          <div class="purpose-actions">
            <router-link to="/agent-activity" class="btn btn-primary btn-sm">
              Open activity feed
            </router-link>
          </div>
        </div>
      </section>

      <section v-if="canViewGrowth" class="bento-purpose bento-demos">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-calendar-check"></i>
        </div>
        <div class="purpose-body">
          <h2>Upcoming demos</h2>
          <p>
            Demos people booked through the shared booking link (home page,
            COI-Verify site, quotes, proposals). Each booking also lands as a
            hot lead and a growth contact.
          </p>
          <ul v-if="demos.length" class="journey-list">
            <li v-for="demo in demos" :key="demo.id">
              <strong>{{ demo.contact_name || demo.contact_email }}</strong>
              <span> · {{ formatWhen(demo.start_at) }}</span>
              <span v-if="demo.status !== 'confirmed'"> · {{ demo.status.replace(/_/g, ' ') }}</span>
            </li>
          </ul>
          <p v-else class="coming-soon">No demos booked yet.</p>
          <div class="purpose-actions">
            <router-link
              :to="{ path: '/agent-activity', query: { event_type: 'DEMO_BOOKED' } }"
              class="btn btn-primary btn-sm"
            >
              View all demos
            </router-link>
            <button
              v-if="demoLink"
              type="button"
              class="btn btn-ghost btn-sm"
              @click="copyDemoLink"
            >{{ demoLinkCopied ? 'Link copied' : 'Copy booking link' }}</button>
          </div>
        </div>
      </section>

      <section v-if="canViewGrowth" class="bento-purpose bento-calendar">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-calendar-alt"></i>
        </div>
        <div class="purpose-body">
          <h2>Your calendar</h2>
          <p>
            Connect Google Calendar or Outlook so a demo is added to the calendar
            that hosts it, and times you are already busy are not offered.
            This is a separate permission from sign-in.
          </p>
          <p v-if="calendarNotice" class="base-note">{{ calendarNotice }}</p>
          <label v-if="calendars.length > 1" class="calendar-pick">
            Calendar these demos use
            <select v-model="selectedCalendarId">
              <option v-for="cal in calendars" :key="cal.id" :value="cal.id">{{ cal.name }}</option>
            </select>
          </label>
          <ul v-if="connections.length" class="journey-list">
            <li v-for="conn in connections" :key="conn.id">
              <strong>{{ conn.provider === 'google' ? 'Google' : 'Outlook' }}</strong>
              <span v-if="conn.account_email"> · {{ conn.account_email }}</span>
              <span v-if="conn.status !== 'active'"> · {{ conn.status }}</span>
              <button type="button" class="btn btn-ghost btn-sm" @click="disconnectCalendar(conn)">
                Disconnect
              </button>
            </li>
          </ul>
          <p v-else class="coming-soon">No calendar connected yet.</p>
          <div class="purpose-actions">
            <button type="button" class="btn btn-primary btn-sm" @click="connectCalendar('google')">
              Connect Google Calendar
            </button>
            <button type="button" class="btn btn-ghost btn-sm" @click="connectCalendar('microsoft')">
              Connect Outlook
            </button>
          </div>
        </div>
      </section>

      <section v-if="canViewGrowth" class="bento-purpose bento-contacts">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-address-book"></i>
        </div>
        <div class="purpose-body">
          <h2>Growth contacts</h2>
          <p>
            Prospect and agent contacts for outreach — filter by stage and
            status, then work the list as you market the platforms.
          </p>
          <div class="purpose-actions">
            <router-link to="/growth-contacts" class="btn btn-primary btn-sm">
              Open contacts
            </router-link>
          </div>
        </div>
      </section>

      <section v-if="canViewGrowth" class="bento-purpose bento-scav">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-user-md"></i>
        </div>
        <div class="purpose-body">
          <h2>SCAV roster for Meta</h2>
          <p>
            Pull SCAV members from MemberClicks, split active members from everyone
            else on that roster, and replace two Facebook / Instagram email-match
            audiences when you are ready to run ads.
          </p>
          <p v-if="scavSummary">
            {{ scavSummary.active_members }} active members ·
            {{ scavSummary.non_members }} non-members on roster ·
            {{ scavSummary.missing_email }} missing email.
          </p>
          <p v-else class="coming-soon">Loading roster counts…</p>
          <p v-if="scavSummary?.last_roster_sync_at" class="base-note">
            Last MemberClicks refresh: {{ formatWhen(scavSummary.last_roster_sync_at) }}
          </p>
          <p v-else-if="scavSummary" class="base-note">
            No roster sync yet — refresh from MemberClicks before uploading to Meta.
          </p>
          <p v-if="scavSummary && !scavSummary.members_ready_for_match" class="base-note">
            Fewer than 100 member emails — Meta match may be weak until the roster grows.
          </p>
          <p v-if="scavNotice" class="base-note">{{ scavNotice }}</p>
          <div class="purpose-actions">
            <router-link to="/marketing/scav-roster" class="btn btn-primary btn-sm">
              View all on roster
            </router-link>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              :disabled="scavBusy"
              @click="syncScavRoster"
            >
              Refresh from MemberClicks
            </button>
            <button
              v-if="scavSummary && !scavSummary.ads_audience_permission_recorded"
              type="button"
              class="btn btn-ghost btn-sm"
              :disabled="scavBusy"
              @click="recordScavPermission"
            >
              Record ad-audience permission
            </button>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              :disabled="!canReplaceScavMeta"
              @click="replaceScavMeta"
            >
              Replace Facebook and Instagram audiences
            </button>
          </div>
        </div>
      </section>

      <section v-if="canViewGrowth" class="bento-purpose bento-base">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-users"></i>
        </div>
        <div class="purpose-body">
          <h2>Marketing base</h2>
          <p v-if="base">
            {{ base.distinct_emails }} distinct emails · {{ base.with_name }} with a name ·
            {{ base.with_agency }} with an agency.
          </p>
          <p v-else>Counting the agent book…</p>
          <p v-if="base?.ready_to_attempt_match" class="base-note">
            Large enough to attempt a Meta, LinkedIn, or X email match.
            A match of about 100 accounts is not guaranteed until a list is uploaded.
          </p>
          <p v-else-if="base" class="base-note">
            Need at least 100 distinct emails before an ad list is worth attempting.
          </p>
        </div>
      </section>

      <section v-if="canViewGrowth" class="bento-purpose bento-journeys">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-route"></i>
        </div>
        <div class="purpose-body">
          <h2>Journeys</h2>
          <p>
            A shelf for journeys we have not built yet. Saving a draft does not
            email anyone or upload a list. An email-list journey cannot be
            uploaded until you record permission to use certificate addresses
            as an ad audience.
          </p>
          <ul v-if="journeys.length" class="journey-list">
            <li v-for="journey in journeys" :key="journey.id">
              <strong>{{ journey.name }}</strong>
              <span> · {{ journey.channel_label }} · {{ journey.targeting_label }}</span>
              <span v-if="journey.upload_blocked"> · Waiting on ad-audience permission</span>
              <span v-else-if="journey.targeting === 'email_list'"> · Permission recorded</span>
              <p v-if="journey.purpose">{{ journey.purpose }}</p>
              <button
                v-if="journey.targeting === 'email_list' && !journey.ads_permission_recorded"
                type="button"
                class="btn btn-ghost btn-sm"
                @click="recordPermission(journey)"
              >Record ad-audience permission</button>
            </li>
          </ul>
          <p v-else class="coming-soon">No drafts yet.</p>
          <form class="journey-form" @submit.prevent="createJourney">
            <input v-model="journeyName" type="text" required placeholder="Journey name" />
            <input v-model="journeyPurpose" type="text" placeholder="Why this journey exists" />
            <select v-model="journeyChannel">
              <option value="email">Email</option>
              <option value="linkedin">LinkedIn</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="x">X</option>
              <option value="multi">Multiple channels</option>
            </select>
            <select v-model="journeyTargeting">
              <option value="email_list">Hashed email list</option>
              <option value="confirmed_profile">Confirmed public profile</option>
            </select>
            <button type="submit" class="btn btn-primary btn-sm">Save draft</button>
          </form>
        </div>
      </section>

      <section class="bento-purpose bento-events bento-placeholder">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-calendar-alt"></i>
        </div>
        <div class="purpose-body">
          <h2>Marketing events</h2>
          <p>
            Planned posts and campaigns from Instagram and LinkedIn once those
            channels are connected. Schedule and review what goes out for each
            product.
          </p>
          <p class="coming-soon" role="status">Coming soon — Instagram &amp; LinkedIn</p>
        </div>
      </section>

      <section class="bento-purpose bento-quotes">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-file-invoice-dollar"></i>
        </div>
        <div class="purpose-body">
          <h2>Quote builder</h2>
          <p>
            Build quotes and proposals for prospects — tiers, ROI, and carrier
            options while working a lead.
          </p>
          <div class="purpose-actions">
            <router-link to="/quote-builder" class="btn btn-primary btn-sm">
              Open quote builder
            </router-link>
          </div>
        </div>
      </section>

      <section class="bento-purpose bento-saved-quotes">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-folder-open"></i>
        </div>
        <div class="purpose-body">
          <h2>Saved quotes</h2>
          <p>
            Review drafts and finalized proposals — edit, export, or pick up
            where you left off with a prospect.
          </p>
          <div class="purpose-actions">
            <router-link to="/quotes" class="btn btn-primary btn-sm">
              Open saved quotes
            </router-link>
            <router-link to="/quote-builder" class="btn btn-ghost btn-sm">
              Create new
            </router-link>
          </div>
        </div>
      </section>

      <section v-if="canViewLmsEngagement" class="bento-purpose bento-lms-engagement">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-chart-line"></i>
        </div>
        <div class="purpose-body">
          <h2>Learner engagement</h2>
          <p>
            Who is active on Learning / CE — scores, drop-off, and follow-up
            opportunities so you can market and retain the Learning product.
          </p>
          <div class="purpose-actions">
            <router-link to="/learning/engagement" class="btn btn-primary btn-sm">
              Open engagement
            </router-link>
            <router-link to="/learning" class="btn btn-ghost btn-sm">
              Learning hub
            </router-link>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import auth from '@/services/auth';
import api from '@/services/api';

export default {
  name: 'AccountMarketingHub',
  data() {
    return {
      base: null,
      journeys: [],
      demos: [],
      demoLink: '',
      demoLinkCopied: false,
      calendars: [],
      connections: [],
      selectedCalendarId: null,
      calendarNotice: '',
      journeyName: '',
      journeyPurpose: '',
      journeyChannel: 'instagram',
      journeyTargeting: 'email_list',
      scavSummary: null,
      scavNotice: '',
      scavBusy: false,
    };
  },
  computed: {
    canViewGrowth() {
      return auth.canViewGrowthEngine();
    },
    canViewLmsEngagement() {
      return auth.isLmsAdmin();
    },
    canReplaceScavMeta() {
      if (!this.scavSummary || this.scavBusy) return false;
      if (!this.scavSummary.ads_audience_permission_recorded) return false;
      if (!this.scavSummary.scav_meta_audiences_flag) return false;
      const meta = this.scavSummary.meta || {};
      return meta.access_token_configured
        && meta.members_audience_configured
        && meta.non_members_audience_configured;
    },
  },
  mounted() {
    this.noteCalendarReturn();
    if (this.canViewGrowth) {
      this.loadMarketing();
      this.loadScavMarketing();
      this.loadDemos();
      this.loadCalendarConnections();
    }
  },
  methods: {
    async loadDemos() {
      const [bookings, link] = await Promise.allSettled([
        api.get('/scheduling/bookings/', { params: { purpose: 'demo', limit: 8 } }),
        api.get('/scheduling/booking-link/', {
          params: { utm_source: 'marketing_hub', utm_medium: 'shared_link' },
        }),
      ]);
      if (bookings.status === 'fulfilled') {
        this.demos = bookings.value.data?.bookings || [];
      }
      if (link.status === 'fulfilled') {
        this.demoLink = link.value.data?.url || '';
      }
    },
    async copyDemoLink() {
      try {
        await navigator.clipboard.writeText(this.demoLink);
        this.demoLinkCopied = true;
        setTimeout(() => { this.demoLinkCopied = false; }, 2000);
      } catch {
        window.prompt('Copy the booking link', this.demoLink);
      }
    },
    noteCalendarReturn() {
      const result = this.$route.query.calendar;
      if (!result) return;
      if (result === 'connected') {
        this.calendarNotice = 'Calendar connected. Busy times on it will not be offered, and confirmed demos will be added to it.';
      } else {
        this.calendarNotice = 'Calendar connection did not finish. You can try again.';
      }
      this.$router.replace({ query: {} });
    },
    async loadCalendarConnections() {
      const [calendars, connections] = await Promise.allSettled([
        api.get('/scheduling/calendars/'),
        api.get('/scheduling/connections/'),
      ]);
      if (calendars.status === 'fulfilled') {
        this.calendars = calendars.value.data?.calendars || [];
        if (!this.selectedCalendarId && this.calendars.length) {
          this.selectedCalendarId = this.calendars[0].id;
        }
      }
      if (connections.status === 'fulfilled') {
        this.connections = (connections.value.data?.connections || [])
          .filter((conn) => conn.status === 'active');
      }
    },
    async connectCalendar(provider) {
      this.calendarNotice = '';
      try {
        const params = {};
        if (this.selectedCalendarId) params.calendar_id = this.selectedCalendarId;
        const response = await api.get(`/scheduling/connections/${provider}/start/`, { params });
        window.location.assign(response.data.authorize_url);
      } catch {
        this.calendarNotice = 'Calendar connect is not available right now. Check that the provider is configured, then try again.';
      }
    },
    async disconnectCalendar(conn) {
      await api.delete(`/scheduling/connections/${conn.id}/`);
      await this.loadCalendarConnections();
      this.calendarNotice = 'Disconnected. Demos will no longer be added to that calendar.';
    },
    formatWhen(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    },
    async loadMarketing() {
      try {
        const baseRes = await api.get('/growth-contacts/base/');
        this.base = baseRes.data;
      } catch {
        this.base = null;
      }
      try {
        const journeyRes = await api.get('/marketing-journeys/');
        this.journeys = journeyRes.data?.results || [];
      } catch {
        this.journeys = [];
      }
    },
    async loadScavMarketing() {
      try {
        const res = await api.get('/scav-marketing/summary/');
        this.scavSummary = res.data;
      } catch {
        this.scavSummary = null;
      }
    },
    async syncScavRoster() {
      this.scavBusy = true;
      this.scavNotice = '';
      try {
        await api.post('/scav-marketing/roster/sync/');
        this.scavNotice = 'MemberClicks refresh queued. Counts update when the sync finishes.';
      } catch (err) {
        this.scavNotice = err.response?.data?.error || 'Could not queue roster refresh.';
      } finally {
        this.scavBusy = false;
        await this.loadScavMarketing();
      }
    },
    async recordScavPermission() {
      this.scavBusy = true;
      this.scavNotice = '';
      try {
        const res = await api.post('/scav-marketing/permission/');
        this.scavSummary = res.data;
        this.scavNotice = 'Permission recorded. You can replace Meta audiences when the flag and audience IDs are set.';
      } catch (err) {
        this.scavNotice = err.response?.data?.error || 'Could not record permission.';
      } finally {
        this.scavBusy = false;
      }
    },
    async replaceScavMeta() {
      this.scavBusy = true;
      this.scavNotice = '';
      try {
        await api.post('/scav-marketing/meta/replace/', { async: true });
        this.scavNotice = 'Meta audience replace queued for members and non-members.';
      } catch (err) {
        this.scavNotice = err.response?.data?.error || 'Meta upload did not start.';
      } finally {
        this.scavBusy = false;
        await this.loadScavMarketing();
      }
    },
    async createJourney() {
      await api.post('/marketing-journeys/', {
        name: this.journeyName,
        purpose: this.journeyPurpose,
        channel: this.journeyChannel,
        targeting: this.journeyTargeting,
      });
      this.journeyName = '';
      this.journeyPurpose = '';
      await this.loadMarketing();
    },
    async recordPermission(journey) {
      await api.patch(`/marketing-journeys/${journey.id}/`, {
        ads_permission_recorded: true,
      });
      await this.loadMarketing();
    },
  },
};
</script>

<style scoped>
.marketing-hub {
  width: 100%;
  max-width: 1120px;
  margin: 0;
}

.hub-header {
  margin-bottom: 1.25rem;
}

.hub-header h1 {
  margin: 0;
  font-size: 1.75rem;
  color: var(--p-ink, #1e293b);
}

.hub-subtitle {
  margin: 0.35rem 0 0;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
  max-width: 42rem;
}

.marketing-bento {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.bento-purpose {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  align-items: start;
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
  min-height: 8.25rem;
}

.bento-placeholder {
  background: #f8fafc;
}

.calendar-pick {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0.5rem 0;
  font-size: 0.85rem;
  color: var(--p-ink-muted, #64748b);
}

.calendar-pick select {
  max-width: 20rem;
}

.base-note,
.journey-list {
  margin: 0.4rem 0 0;
  color: var(--p-ink-muted, #64748b);
  font-size: 0.875rem;
}

.journey-list {
  list-style: none;
  padding: 0;
}

.journey-form {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.75rem;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}

.purpose-body h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  color: var(--p-ink, #1e293b);
}

.purpose-body p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--p-ink-muted, #64748b);
}

.purpose-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.coming-soon {
  margin-top: 0.85rem !important;
  font-size: 0.8rem !important;
  font-weight: 600;
  color: #92400e !important;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 0.45rem;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  padding: 0.4rem 0.75rem;
}

.btn-sm {
  font-size: 0.8rem;
}

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: #fff;
  border-color: var(--p-accent, #1e3a5f);
}

.btn-primary:hover {
  filter: brightness(1.05);
}

.btn-ghost {
  background: transparent;
  color: var(--p-accent, #1e3a5f);
  border-color: #e2e8f0;
}

.btn-ghost:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

@media (max-width: 767px) {
  .marketing-bento {
    grid-template-columns: 1fr;
  }
}
</style>
